import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Target, Heart, ArrowRight } from 'lucide-react';
import {
  getPageContent,
  getValues,
  getTeamMembers,
  getJourneyMilestones
} from '../utils/contentManager';

const About = () => {
  const [pageContent, setPageContent] = useState({});
  const [values, setValues] = useState([]);
  const [team, setTeam] = useState([]);
  const [milestones, setMilestones] = useState([]);

  useEffect(() => {
    setPageContent(getPageContent('about'));
    setValues(getValues());
    setTeam(getTeamMembers());
    setMilestones(getJourneyMilestones());
  }, []);

  const getIconComponent = (iconName) => {
    const icons = {
      Target,
      Heart,
      Award,
      Users
    };
    return icons[iconName] || Target;
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 102, 255, 0.9), rgba(0, 51, 153, 0.9)), url('https://images.unsplash.com/photo-1573497701175-00c200fd57f0?w=1920&h=1080&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {pageContent.title || 'About'} <span className="text-indigo-200">OHA System</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-indigo-100">
              {pageContent.subtitle ||
                'We are passionate technologists and strategists dedicated to empowering small businesses and startups with the transformative power of AI and strategic consulting.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
                {pageContent.missionTitle || 'Our'} <span className="text-gradient">Mission</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                {pageContent.mission ||
                  'To democratize access to cutting-edge AI technology and strategic consulting, enabling small businesses and startups to compete and thrive in the digital economy.'}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {pageContent.missionDescription ||
                  'We believe that every business, regardless of size, deserves access to the same transformative technologies that drive success for industry giants. Our mission is to level the playing field through innovative AI solutions and strategic guidance.'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="glass-effect p-8 rounded-2xl"
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                {pageContent.visionTitle || 'Our Vision'}
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {pageContent.vision ||
                  'To be the leading force in business transformation, where AI and human expertise combine to create unprecedented value and growth opportunities.'}
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-800 rounded-full"></div>
                  <span className="text-gray-700">Accessible AI for all businesses</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-800 rounded-full"></div>
                  <span className="text-gray-700">Sustainable growth through innovation</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-800 rounded-full"></div>
                  <span className="text-gray-700">Transformative business outcomes</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {pageContent.valuesTitle || 'Our'} <span className="text-gradient">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent.valuesSubtitle ||
                'These core values guide every decision we make and every solution we deliver.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = getIconComponent(value.icon);
              return (
                <motion.div
                  key={value.id || index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  whileHover={{ y: -10 }}
                  className="bg-white p-8 rounded-2xl shadow-premium hover:shadow-glow transition-all duration-300 text-center"
                >
                  <div className="gradient-background w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {pageContent.teamTitle || 'Meet Our'} <span className="text-gradient">Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent.teamSubtitle ||
                'Experienced professionals passionate about leveraging technology to drive business success.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.id || index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                whileHover={{ y: -10 }}
                className="bg-white p-8 rounded-2xl shadow-premium hover:shadow-glow transition-all duration-300 text-center"
              >
                <img
                  src={
                    member.image ||
                    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face'
                  }
                  alt={member.name}
                  className="w-24 h-24 rounded-full object-cover mx-auto mb-6 border-4 border-blue-100"
                />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-blue-600 font-semibold mb-4">{member.role}</p>
                <p className="text-gray-600 leading-relaxed">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {pageContent.journeyTitle || 'Our'} <span className="text-gradient">Journey</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent.journeySubtitle ||
                'Key milestones that have shaped our company and the value we deliver to clients.'}
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.id || index}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  className="relative flex items-start space-x-8"
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm relative z-10">
                    {milestone.year}
                  </div>
                  <div className="flex-1 bg-white p-6 rounded-2xl shadow-premium relative">
                    <div className="absolute top-8 left-0 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-white transform -translate-x-2"></div>
                    <p className="text-gray-700 leading-relaxed">{milestone.event}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 gradient-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} className="space-y-8">
            <h2 className="text-4xl md:text-6xl font-bold text-white">
              {pageContent.ctaTitle || 'Join Our Success Story'}
            </h2>
            <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
              {pageContent.ctaSubtitle ||
                "Ready to transform your business with our expertise? Let's start the conversation."}
            </p>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(255, 255, 255, 0.3)' }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-800 px-12 py-4 rounded-xl font-bold text-xl shadow-premium hover:shadow-glow transition-all duration-300 inline-flex items-center space-x-3"
            >
              <span>{pageContent.ctaButtonText || 'Get Free Basic Consultation'}</span>
              <ArrowRight size={24} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
